import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../lib/api-client';
import type { paths } from '../lib/api-types';
import { useApiStore } from '../store/api';
import { useAuthStore } from '../store/auth';

type Message =
  paths['/v1/conversations/{conversationId}/messages']['get']['responses']['200']['content']['application/json']['messages'][0];

export interface UseMessagesReturn {
  messages: Message[];
  loading: boolean;
  error: Error | null;
  sendMessage: (content: string, parentId?: string) => Promise<Message>;
  sendingMessage: boolean;
  editMessage: (id: string, content: string) => Promise<Message>;
  editingMessage: boolean;
  deleteMessage: (id: string) => Promise<void>;
  deletingMessage: boolean;
  toggleReaction: (messageId: string, emoji: string, currentlyReacted: boolean) => Promise<void>;
  refreshMessages: () => Promise<void>;
}

/**
 * Message list management hook for a conversation using TanStack Query
 *
 * @example
 * ```typescript
 * const { messages, loading, sendMessage } = useMessages(conversationId);
 * ```
 */
export function useMessages(conversationId: string | null): UseMessagesReturn {
  const apiUrl = useApiStore((state) => state.apiUrl);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();

  // Query key for this conversation's messages
  const queryKey = ['messages', conversationId];

  // Fetch messages using TanStack Query
  const {
    data: messages = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!conversationId) {
        return [];
      }

      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.GET(
        '/v1/conversations/{conversationId}/messages',
        {
          params: { path: { conversationId } },
        }
      );

      if (apiError || !data) {
        throw new Error('Failed to load messages');
      }

      return data.messages;
    },
    enabled: !!conversationId && isAuthenticated,
    staleTime: 1000, // Consider data fresh for 1 second
  });

  // Send message mutation with optimistic updates
  const sendMessageMutation = useMutation({
    mutationFn: async ({ content, parentId }: { content: string; parentId?: string }) => {
      if (!conversationId) {
        throw new Error('Cannot send message without conversation');
      }

      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.POST(
        '/v1/conversations/{conversationId}/messages',
        {
          params: { path: { conversationId } },
          body: {
            content,
            parent_message_id: parentId,
          },
        }
      );

      if (apiError || !data) {
        throw new Error('Failed to send message');
      }

      return data;
    },
    onMutate: async ({ content }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<Message[]>(queryKey);

      // Optimistically update to the new value
      const tempMessage: Message = {
        id: `temp-${Date.now()}`,
        conversation_id: conversationId!,
        sender_id: user?.sub ?? 'current-user',
        sender_username: user?.username ?? 'You',
        sender_display_name: null,
        sender_avatar_url: null,
        parent_message_id: null,
        content,
        urgency: 'normal' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        edited: false,
        last_edited_at: null,
      };

      queryClient.setQueryData<Message[]>(queryKey, (old = []) => [...old, tempMessage]);

      // Return context for rollback
      return { previousMessages };
    },
    onError: (_err, _variables, context) => {
      // Roll back to previous state on error
      if (context?.previousMessages) {
        queryClient.setQueryData(queryKey, context.previousMessages);
      }
    },
    onSuccess: (newMessage) => {
      // Replace the optimistic temp message with real server data.
      // Do NOT call invalidateQueries here — the GSI-based refetch can
      // return stale (empty) data due to DynamoDB GSI eventual consistency,
      // overwriting the cache and causing messages to vanish.
      // SSE handles real-time sync from other participants instead.
      queryClient.setQueryData<Message[]>(queryKey, (old = []) => {
        const withoutTemp = old.filter((msg) => !msg.id.startsWith('temp-'));
        return [...withoutTemp, newMessage as Message];
      });
    },
  });

  // Edit message mutation
  const editMessageMutation = useMutation({
    mutationFn: async ({ id, content }: { id: string; content: string }) => {
      if (!conversationId) {
        throw new Error('Cannot edit message without conversation');
      }

      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.PATCH(
        '/v1/conversations/{conversationId}/messages/{messageId}',
        {
          params: { path: { conversationId, messageId: id } },
          body: { content },
        }
      );

      if (apiError || !data) {
        throw new Error('Failed to edit message');
      }

      return data;
    },
    onSuccess: (updatedMessage) => {
      // Update the message in the cache
      queryClient.setQueryData<Message[]>(queryKey, (old = []) =>
        old.map((msg) => (msg.id === updatedMessage.id ? updatedMessage : msg))
      );
    },
  });

  // Delete message mutation
  const deleteMessageMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!conversationId) {
        throw new Error('Cannot delete message without conversation');
      }

      const client = createApiClient(apiUrl);
      const { error: apiError } = await client.DELETE(
        '/v1/conversations/{conversationId}/messages/{messageId}',
        {
          params: { path: { conversationId, messageId: id } },
        }
      );

      if (apiError) {
        throw new Error('Failed to delete message');
      }

      return id;
    },
    onSuccess: (deletedId) => {
      // Remove the message from the cache
      queryClient.setQueryData<Message[]>(queryKey, (old = []) =>
        old.filter((msg) => msg.id !== deletedId)
      );
    },
  });

  // Toggle reaction mutation with optimistic updates
  const toggleReactionMutation = useMutation({
    mutationFn: async ({
      messageId,
      emoji,
      currentlyReacted,
    }: {
      messageId: string;
      emoji: string;
      currentlyReacted: boolean;
    }) => {
      if (!conversationId) {
        throw new Error('Cannot toggle reaction without conversation');
      }

      const client = createApiClient(apiUrl);

      if (currentlyReacted) {
        // Remove reaction
        const { error: apiError } = await client.DELETE(
          '/v1/conversations/{conversationId}/messages/{messageId}/reactions/{emoji}',
          {
            params: { path: { conversationId, messageId, emoji } },
          }
        );
        if (apiError) {
          throw new Error('Failed to remove reaction');
        }
      } else {
        // Add reaction
        const { error: apiError } = await client.POST(
          '/v1/conversations/{conversationId}/messages/{messageId}/reactions',
          {
            params: { path: { conversationId, messageId } },
            body: { emoji },
          }
        );
        if (apiError) {
          throw new Error('Failed to add reaction');
        }
      }

      return { messageId, emoji, currentlyReacted };
    },
    onMutate: async ({ messageId, emoji, currentlyReacted }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<Message[]>(queryKey);

      // Optimistically update the reactions
      queryClient.setQueryData<Message[]>(queryKey, (old = []) =>
        old.map((msg) => {
          if (msg.id !== messageId) return msg;

          const reactions = msg.reactions ?? [];

          if (currentlyReacted) {
            // Remove reaction - decrement count or remove if count becomes 0
            const updatedReactions = reactions
              .map((r) => {
                if (r.emoji !== emoji) return r;
                return { ...r, count: r.count - 1, user_reacted: false };
              })
              .filter((r) => r.count > 0);

            return { ...msg, reactions: updatedReactions };
          } else {
            // Add reaction - increment count or add new
            const existing = reactions.find((r) => r.emoji === emoji);
            if (existing) {
              const updatedReactions = reactions.map((r) =>
                r.emoji === emoji ? { ...r, count: r.count + 1, user_reacted: true } : r
              );
              return { ...msg, reactions: updatedReactions };
            } else {
              return {
                ...msg,
                reactions: [...reactions, { emoji, count: 1, user_reacted: true }],
              };
            }
          }
        })
      );

      // Return context for rollback
      return { previousMessages };
    },
    onError: (_err, _variables, context) => {
      // Roll back to previous state on error
      if (context?.previousMessages) {
        queryClient.setQueryData(queryKey, context.previousMessages);
      }
    },
    // Note: We intentionally do NOT invalidate queries on success
    // The optimistic update is the source of truth until SSE refreshes
  });

  return {
    messages,
    loading,
    error: error as Error | null,
    sendMessage: async (content: string, parentId?: string) => {
      const result = await sendMessageMutation.mutateAsync({ content, parentId });
      return result;
    },
    sendingMessage: sendMessageMutation.isPending,
    editMessage: async (id: string, content: string) => {
      const result = await editMessageMutation.mutateAsync({ id, content });
      return result;
    },
    editingMessage: editMessageMutation.isPending,
    deleteMessage: async (id: string) => {
      await deleteMessageMutation.mutateAsync(id);
    },
    deletingMessage: deleteMessageMutation.isPending,
    toggleReaction: async (messageId: string, emoji: string, currentlyReacted: boolean) => {
      await toggleReactionMutation.mutateAsync({ messageId, emoji, currentlyReacted });
    },
    refreshMessages: async () => {
      await refetch();
    },
  };
}
